/**
 * Section "Fichiers" rendue sur la page projet.
 *
 *  - Liste des files attachés au projet (status visible : pending/ready/errored)
 *  - Bouton "Téléverser" → ouvre `FileUploadDialog` avec parent = project_id
 *  - Bouton "Télécharger" sur chaque ligne → demande une signed URL fraîche
 *    et ouvre dans un nouvel onglet (le browser fera le download).
 *  - Bouton "Supprimer" sur chaque ligne (soft-delete côté API).
 *
 * Périmètre 9.A : on liste UNIQUEMENT les fichiers attachés au projet
 * (filter project_id=...). Les fichiers attachés via une submission seront
 * affichés dans la section dédiée ProjectSubmissionsSection (9.B).
 *
 * Décision UX : on n'affiche pas les fichiers `kind='submission'` ici parce
 * qu'ils ne devraient pas exister sans submission_id rattachée. Si l'API en
 * retourne quand même (cas pathologique), ils seront bien rendus, juste
 * libellés via i18n `files.kinds.submission`.
 */

import { Schemas } from "@kollectivia/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { FileDocumentContext } from "../../../components/FileDocumentContext";
import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import {
  type FileRow,
  useDeleteFile,
  useFileDownloadUrl,
  useFiles,
} from "../../../lib/queries/files";
import { toast } from "../../../lib/toast";

import { FileUploadDialog } from "./FileUploadDialog";

// -----------------------------------------------------------------------------
// Section principale
// -----------------------------------------------------------------------------

export function ProjectFilesSection({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const [dialogOpen, setDialogOpen] = useState(false);

  const filesQuery = useFiles({ project_id: projectId, limit: 50 });
  const items = filesQuery.data?.items ?? [];

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <header className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
          {t("files.section.title")}
        </h2>
        <button type="button" onClick={() => setDialogOpen(true)} className="btn-primary text-xs">
          {t("files.actions.upload")}
        </button>
      </header>

      {filesQuery.isLoading ? (
        <p className="text-sm text-slate-400">{t("app.loading")}</p>
      ) : filesQuery.error ? (
        <p className="text-sm text-red-700">
          {filesQuery.error instanceof ApiException
            ? getApiErrorMessage(filesQuery.error, lang) || t("errors.generic")
            : t("errors.generic")}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400">{t("files.section.empty")}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((f) => (
            <FileLine key={f.id} file={f} />
          ))}
        </ul>
      )}

      <FileUploadDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        parent={{ project_id: projectId }}
        // Sur un projet, on prépare les pièces côté client/courtier.
        // Les `submission` seront uploadés depuis la fiche soumission (9.B).
        allowedKinds={["client-request", "attachment"]}
        defaultKind="client-request"
        onUploaded={() => {
          // useCompleteFileUpload invalide déjà filesKeys.all,
          // mais on force aussi un refetch immédiat pour le feedback UX.
          void filesQuery.refetch();
        }}
      />
    </section>
  );
}

// -----------------------------------------------------------------------------
// Ligne fichier
// -----------------------------------------------------------------------------

function FileLine({ file }: { file: FileRow }) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const downloadUrl = useFileDownloadUrl();
  const remove = useDeleteFile();

  const onDownload = async () => {
    try {
      const res = await downloadUrl.mutateAsync(file.id);
      // On ouvre dans un nouvel onglet — le browser détectera Content-Disposition
      // et démarrera le download (Supabase Storage attache un Content-Disposition
      // attachment quand `download` query param est présent ; mais on a une
      // signed URL pure, donc le PDF s'ouvre inline. Acceptable pour 9.A —
      // l'utilisateur peut Cmd+S. À améliorer en 11+ si besoin avec un
      // anchor + download attribute.)
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(
        e instanceof ApiException
          ? getApiErrorMessage(e, lang) || t("errors.generic")
          : t("errors.generic"),
      );
    }
  };

  const onDelete = async () => {
    if (!confirm(t("files.actions.delete_confirm", { name: file.file_name }))) {
      return;
    }
    try {
      await remove.mutateAsync(file.id);
      toast.success(t("files.actions.delete_done"));
    } catch (e) {
      toast.error(
        e instanceof ApiException
          ? getApiErrorMessage(e, lang) || t("errors.generic")
          : t("errors.generic"),
      );
    }
  };

  return (
    <li className="grid grid-cols-[2.25rem_minmax(0,1fr)] items-start gap-3 py-2.5 sm:grid-cols-[2.25rem_minmax(0,1fr)_auto]">
      <div className="bg-brand-100 text-brand-800 grid h-9 w-9 shrink-0 place-items-center rounded text-[10px] font-bold">
        {file.mime_type === "application/pdf"
          ? "PDF"
          : file.mime_type === Schemas.XLSX_MIME
            ? "XLSX"
            : "—"}
      </div>
      <div className="min-w-0 flex-1">
        <p className="break-words text-sm font-medium text-slate-800">
          {t(`files.kinds.${file.kind}`, {
            defaultValue: t(`files.generated_kinds.${file.kind}`, {
              defaultValue: t("files.generic_kind"),
            }),
          })}
        </p>
        <FileDocumentContext context={file.document_context} uploadedAt={file.uploaded_at} />
        <details className="text-xs text-slate-600">
          <summary className="min-h-11 cursor-pointer py-3 focus-visible:ring-2">
            {t("flow.file_details")}
          </summary>
          <p className="break-words [overflow-wrap:anywhere]">{file.file_name}</p>
        </details>
        <p className="text-xs text-slate-500">
          {formatBytes(file.size_bytes)}
          {typeof file.page_count === "number"
            ? ` · ${t("files.pages_count", { count: file.page_count })}`
            : null}
          {" · "}
          <FileStatusBadge status={file.status} />
        </p>
      </div>
      <div className="col-span-2 flex flex-wrap items-center gap-2 sm:col-span-1 sm:col-start-3 sm:row-start-1">
        <button
          type="button"
          onClick={onDownload}
          disabled={file.status !== "ready" || downloadUrl.isPending}
          className="text-brand-700 hover:bg-brand-50 min-h-11 rounded px-2 py-1 text-xs disabled:opacity-40"
        >
          {downloadUrl.isPending ? t("app.loading") : t("files.actions.download")}
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={remove.isPending}
          className="min-h-11 rounded px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-40"
        >
          {remove.isPending ? t("app.loading") : t("common.delete")}
        </button>
      </div>
    </li>
  );
}

// -----------------------------------------------------------------------------
// Badge status
// -----------------------------------------------------------------------------

function FileStatusBadge({ status }: { status: FileRow["status"] }) {
  const { t } = useTranslation();
  const className =
    status === "ready"
      ? "text-green-700"
      : status === "errored"
        ? "text-red-700"
        : "text-amber-700";
  return <span className={className}>{t(`files.statuses.${status}`)}</span>;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}
